import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import ShareGameModal from '../features/ShareGameModal/ShareGameModal';
import { ActiveBet } from '../features/active-bet/active-bet';
import { PlayerCard } from '../features/player-card/player-card';
import { RoomPrizeImage } from '../features/room-prize-image/room-prize-image';
import clsx from 'clsx';
import { audioManager } from 'lib/audio/audio-manager';
import { AUDIO_TYPES } from 'lib/audio/audio.types';

import useCreateJoinRoomEvents from 'modules/Room/Hooks/useCreateJoinRoomEvents';
import HistoryMoves from 'modules/Room/features/HistoryMoves/HistoryMoves';
import { Score } from 'modules/Room/features/Score/Score';

import { Header } from 'shared/components/sections/header/header';
import { OptimizedImage } from 'shared/components/ui/optimized-image/optimized-image';
import { NULL_ADDRESS } from 'shared/constants/constants';
import { RoomStatusEnum, RoundOutcome, WinnerStatusEnum } from 'shared/constants/shared-enums';
import { useAccount } from 'shared/hooks/use-web3';
import { useTransactionReceipt } from 'shared/hooks/useTransactionReceipt';
import { usePlayerGamesStore } from 'shared/store/playerGamesStore';
import { useRoomStore } from 'shared/store/roomStore';

import floor from 'assets/png/floor.png?format=webp;avif&as=picture';
import humanLeft from 'assets/png/human-left.png?format=webp;avif&as=picture';
import humanRight from 'assets/png/human-right.png?format=webp;avif&as=picture';

interface DesktopRoomProps {
  invitedRoomId: number | null;
  handleRevengeRequest: () => void;
  handleAcceptInvite: (roomId: number) => void;
}

const DesktopRoom = ({
  invitedRoomId,
  handleRevengeRequest,
  handleAcceptInvite,
}: DesktopRoomProps) => {
  const { chainId } = useAccount();

  const { setCloseHash, closeHash } = usePlayerGamesStore();
  const { isSuccess: isSuccessCloseUnusedRoom } = useTransactionReceipt(
    closeHash?.[chainId!]?.hash,
    closeHash?.[chainId!]?.date,
  );
  useEffect(() => {
    if (isSuccessCloseUnusedRoom) {
      setCloseHash(chainId!, null);
    }
  }, [isSuccessCloseUnusedRoom]);
  useCreateJoinRoomEvents();

  const { activeRoomId } = useParams();
  const [animationsTriggered, setAnimationsTriggered] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [shareGameModalOpen, setShareGameModalOpen] = useState<boolean>(false);
  const [amIWinner, setAmIWInner] = useState(false);
  const {
    playerA,
    playerB,
    amIPlayerA,
    someoneElseGame,
    roundCounter,
    roomStatus,
    winsTo,
    winnerStatus,
    lastRoundOutcome,
  } = useRoomStore();

  const isRoomActive = useMemo(
    () =>
      activeRoomId &&
      playerB.address !== NULL_ADDRESS &&
      (roomStatus === RoomStatusEnum.ReadyForGame || roomStatus === RoomStatusEnum.ReadyForReveal),
    [activeRoomId, playerB, roomStatus],
  );

  useEffect(() => {
    if (isRoomActive) {
      setAnimationsTriggered(true);
    }
  }, [isRoomActive]);

  useEffect(() => {
    const isShowModal =
      (amIPlayerA && winnerStatus === WinnerStatusEnum.WinnerPlayerA) ||
      (!amIPlayerA && winnerStatus === WinnerStatusEnum.WinnerPlayerB);
    if (isShowModal) {
      const timer = setTimeout(() => {
        setShareGameModalOpen(true);
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [winnerStatus]);

  useEffect(() => {
    if (roomStatus === RoomStatusEnum.Closed) {
      audioManager.playMusic(AUDIO_TYPES.LOBBY_BACKGROUND);
    }
  }, [roomStatus]);

  useEffect(() => {
    if (isRoomActive) {
      if (roundCounter === 1) {
        audioManager.playSound(AUDIO_TYPES.GAME_START);
      }
      audioManager.playMusic(AUDIO_TYPES.GAME_BACKGROUND);
    }
  }, [isRoomActive, roundCounter]);

  useEffect(() => {
    const outcomeSoundMap: Partial<Record<RoundOutcome, AUDIO_TYPES>> = {
      [RoundOutcome.Win]: AUDIO_TYPES.ROUND_WIN,
      [RoundOutcome.Lose]: AUDIO_TYPES.ROUND_LOSS,
      [RoundOutcome.Draw]: AUDIO_TYPES.DRAW,
    };

    const soundType = outcomeSoundMap[lastRoundOutcome];
    if (soundType) {
      audioManager.playSound(soundType);
    }
  }, [lastRoundOutcome]);

  useEffect(() => {
    if (roomStatus === RoomStatusEnum.Closed && playerB.address !== NULL_ADDRESS) {
      const didIWin =
        (!amIPlayerA && winnerStatus === WinnerStatusEnum.WinnerPlayerB) ||
        (amIPlayerA && winnerStatus === WinnerStatusEnum.WinnerPlayerA) ||
        winnerStatus === WinnerStatusEnum.PlayerAExitRoom ||
        (amIPlayerA && winnerStatus === WinnerStatusEnum.TechnicalWinnerA) ||
        (!amIPlayerA && winnerStatus === WinnerStatusEnum.WinnerPlayerB);
      if (didIWin) {
        setAmIWInner(true);
      }
    }
  }, [roomStatus, winnerStatus, amIPlayerA, playerB.address]);

  useEffect(() => {
    if (roomStatus === RoomStatusEnum.Closed && playerB.address !== NULL_ADDRESS) {
      audioManager.playSound(amIWinner ? AUDIO_TYPES.MATCH_WIN : AUDIO_TYPES.MATCH_DEFEAT);
    }
  }, [amIWinner, roomStatus]);

  const classNamePlayerACard = someoneElseGame ? 'order-1' : amIPlayerA ? 'order-1' : 'order-3';
  const classNamePlayerBCard = someoneElseGame ? 'order-3' : amIPlayerA ? 'order-3' : 'order-1';
  return (
    <div className="relative h-full w-full overflow-hidden">
      <div className="flex h-screen flex-col items-center overflow-auto">
        <div className="z-50 mx-auto mb-4 w-full max-w-[1920px] px-8 pt-[32px] 2xl:mb-12 xlg:px-[64px]">
          <Header
            title={
              <>
                Live Game
                <div className="h-[16px] w-[16px] animate-pulse rounded-[50%] bg-[#0AF33D]" />
              </>
            }
          />
        </div>

        <div
          ref={containerRef}
          className="flex max-h-[1080px] w-full max-w-[1840px] flex-1 flex-col items-stretch justify-around gap-4 px-8 pb-5 sm:flex-row 2xl:gap-11 2xl:px-[70px] 2xl:pb-8"
        >
          <div
            className={clsx(
              classNamePlayerACard,
              'z-40 max-w-[630px] flex-1',
              animationsTriggered && 'animate-appear-with-blur',
            )}
          >
            <PlayerCard
              position={classNamePlayerACard}
              playerAddress={playerA.address}
              winsTo={Number(winsTo)}
              onShareClick={() => setShareGameModalOpen(true)}
              invitedRoomId={invitedRoomId}
              onRevengeClick={handleRevengeRequest}
              onAcceptInvite={handleAcceptInvite}
            />
          </div>
          <div
            className={clsx(
              `z-30 order-2 flex w-full flex-col gap-2 sm:min-w-[280px] sm:max-w-[350px]`,
              animationsTriggered && 'animate-appear',
            )}
          >
            <Score />
            <ActiveBet
              amIWinner={amIWinner}
              isGameFinished={roomStatus === RoomStatusEnum.Closed}
            />
            <HistoryMoves />

            {activeRoomId && <RoomPrizeImage animationsTriggered={animationsTriggered} />}
          </div>
          <div
            className={clsx(
              classNamePlayerBCard,
              'z-40 max-w-[630px] flex-1',
              animationsTriggered && 'animate-appear-with-blur',
            )}
          >
            <PlayerCard
              position={classNamePlayerBCard}
              playerAddress={playerB.address}
              winsTo={Number(winsTo)}
              onShareClick={() => setShareGameModalOpen(true)}
              invitedRoomId={invitedRoomId}
              onRevengeClick={handleRevengeRequest}
              onAcceptInvite={handleAcceptInvite}
            />
          </div>
          <OptimizedImage
            imageData={floor}
            alt="floor"
            className="absolute bottom-0 left-0 right-0 z-0 h-[20%] w-full"
          />
          <OptimizedImage
            imageData={humanLeft}
            alt="guy"
            className={clsx(
              'z-1 absolute bottom-0 left-0 top-0 h-full max-w-[45%] opacity-60',
              animationsTriggered && 'animate-slide-in-from-left',
            )}
            style={{ filter: 'blur(6px)' }}
          />
          {playerB.address !== NULL_ADDRESS && activeRoomId && (
            <OptimizedImage
              imageData={humanRight}
              alt="guy"
              className={clsx(
                'z-1 absolute bottom-0 right-0 top-0 h-full max-w-[45%] opacity-60',
                animationsTriggered
                  ? 'animate-slide-in-from-right'
                  : 'animate-fast-appear-with-blur',
              )}
              style={{ filter: 'blur(6px)' }}
            />
          )}
        </div>
      </div>
      <ShareGameModal open={shareGameModalOpen} setOpen={setShareGameModalOpen} />
    </div>
  );
};

export default DesktopRoom;
